import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FileWarning, AlertTriangle, Download, RefreshCw, ClipboardCheck, Calendar } from "lucide-react";
import { fmtDate } from "@/lib/cc";

const COLORS = {
  clear: "#10b981",
  verified: "#10b981",
  pending: "#f59e0b",
  awaiting_upload: "#f59e0b",
  not_clear: "#ef4444",
  not_verified: "#ef4444",
  expired: "#7c2d12",
};

function StatusBar({ data, total }) {
  if (!total) return <div className="h-2 bg-[var(--cc-surface)] rounded-full" />;
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-[var(--cc-surface)]">
      {Object.entries(data).map(([k, v]) => {
        const pct = (v / total) * 100;
        if (pct === 0) return null;
        return <div key={k} title={`${k}: ${v} (${pct.toFixed(0)}%)`} style={{ width: `${pct}%`, background: COLORS[k] || "#94a3b8" }} />;
      })}
    </div>
  );
}

export default function ComplianceDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/dashboard");
      setData(data);
    } catch { toast.error("Failed to load compliance dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="cc-card p-10 text-center text-[var(--cc-text-soft)]">Loading compliance dashboard…</div>;

  const dbsTotal = Object.values(data.dbs_status.breakdown).reduce((a, b) => a + b, 0) || 1;
  const rtwTotal = Object.values(data.rtw_status.breakdown).reduce((a, b) => a + b, 0) || 1;
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="fade-up space-y-6" data-testid="compliance-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Compliance</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><ShieldCheck className="w-8 h-8" /> CQC Compliance Reporting</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Outstanding pre-employment checks · DBS / RTW status · visa expiry monitoring · CQC checklist. <strong>Scope:</strong> candidates at the Offer or Hired stage.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="cc-card p-5" data-testid="kpi-outstanding">
          <div className="flex items-center justify-between"><FileWarning className="w-5 h-5 text-[var(--cc-primary)]" /><span className="label-eyebrow">Outstanding</span></div>
          <div className="font-display text-4xl font-bold mt-2">{data.outstanding_checks.total}</div>
          <div className="text-xs text-[var(--cc-text-soft)] mt-1">{data.outstanding_checks.overdue} overdue · {data.outstanding_checks.due_in_7_days} due in 7d</div>
        </div>
        <div className="cc-card p-5" data-testid="kpi-dbs">
          <div className="flex items-center justify-between"><ShieldCheck className="w-5 h-5 text-[var(--cc-primary)]" /><span className="label-eyebrow">DBS clear</span></div>
          <div className="font-display text-4xl font-bold mt-2">{data.dbs_status.breakdown.clear}</div>
          <div className="text-xs text-[var(--cc-text-soft)] mt-1">{data.dbs_status.expiring_in_3_months} expiring (3m) · {data.dbs_status.expired} expired</div>
        </div>
        <div className="cc-card p-5" data-testid="kpi-rtw">
          <div className="flex items-center justify-between"><ClipboardCheck className="w-5 h-5 text-[var(--cc-primary)]" /><span className="label-eyebrow">RTW verified</span></div>
          <div className="font-display text-4xl font-bold mt-2">{data.rtw_status.breakdown.verified}</div>
          <div className="text-xs text-[var(--cc-text-soft)] mt-1">{data.rtw_status.expiring_in_3_months} expiring (3m) · {data.rtw_status.expired} expired</div>
        </div>
        <div className="cc-card p-5" data-testid="kpi-cqc">
          <div className="flex items-center justify-between"><AlertTriangle className="w-5 h-5 text-[var(--cc-primary)]" /><span className="label-eyebrow">CQC checklist</span></div>
          <div className="font-display text-4xl font-bold mt-2">{data.cqc_checklist.overall_pct}%</div>
          <div className="text-xs text-[var(--cc-text-soft)] mt-1">
            {Object.values(data.cqc_checklist).filter((v) => v === true).length - 0}/{Object.keys(data.cqc_checklist).filter((k) => k !== "overall_pct").length} green
          </div>
        </div>
      </div>

      {/* DBS + RTW breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="cc-card p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-display font-bold text-lg">DBS status</h3>
            <a className="btn-ghost text-xs" href={`${apiBase}/api/compliance/export/dbs`} target="_blank" rel="noreferrer" data-testid="export-dbs"><Download className="w-3.5 h-3.5" /> CSV</a>
          </div>
          <StatusBar data={data.dbs_status.breakdown} total={dbsTotal} />
          <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
            {Object.entries(data.dbs_status.breakdown).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[k] || "#94a3b8" }} />
                <span className="capitalize text-[var(--cc-text-soft)]">{k.replace(/_/g, " ")}</span>
                <span className="ml-auto font-semibold">{v}</span>
              </div>
            ))}
          </div>
          <div className="cc-divider mt-4 pt-3 text-xs text-[var(--cc-text-soft)] flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" />
            {data.dbs_status.expiring_in_1_month} expire in 1m · {data.dbs_status.expiring_in_3_months} in 3m · {data.dbs_status.expired} already expired
          </div>
        </div>

        <div className="cc-card p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-display font-bold text-lg">Right to Work status</h3>
            <a className="btn-ghost text-xs" href={`${apiBase}/api/compliance/export/rtw`} target="_blank" rel="noreferrer" data-testid="export-rtw"><Download className="w-3.5 h-3.5" /> CSV</a>
          </div>
          <StatusBar data={data.rtw_status.breakdown} total={rtwTotal} />
          <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
            {Object.entries(data.rtw_status.breakdown).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[k] || "#94a3b8" }} />
                <span className="capitalize text-[var(--cc-text-soft)]">{k.replace(/_/g, " ")}</span>
                <span className="ml-auto font-semibold">{v}</span>
              </div>
            ))}
          </div>
          {Object.keys(data.rtw_status.by_visa_category).length > 0 && (
            <>
              <div className="cc-divider mt-4 pt-3 label-eyebrow">By visa category</div>
              <table className="w-full text-xs mt-2">
                <thead><tr className="text-left text-[var(--cc-text-soft)]"><th className="py-1">Category</th><th>Verified</th><th>Pending</th><th>Total</th></tr></thead>
                <tbody>
                  {Object.entries(data.rtw_status.by_visa_category).map(([k, v]) => (
                    <tr key={k} className="border-t border-[var(--cc-border)]"><td className="py-1 capitalize">{k.replace(/_/g, " ")}</td><td>{v.verified}</td><td>{v.pending}</td><td>{v.total}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* CQC checklist */}
      <div className="cc-card p-5" data-testid="cqc-checklist">
        <h3 className="font-display font-bold text-lg mb-3">CQC compliance checklist</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(data.cqc_checklist).filter(([k]) => k !== "overall_pct").map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 p-2 rounded-md bg-[var(--cc-surface)]">
              <span className={`w-3 h-3 rounded-full ${v ? "bg-emerald-500" : "bg-red-500"}`} />
              <span className="text-sm capitalize">{k.replace(/_/g, " ")}</span>
              <span className={`ml-auto pill ${v ? "pill-success" : "pill-danger"}`}>{v ? "OK" : "Action"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Outstanding checks table */}
      <div className="cc-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--cc-border)] flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Outstanding pre-employment checks ({data.outstanding_checks.total})</h3>
          <a className="btn-ghost text-xs" href={`${apiBase}/api/compliance/export/outstanding`} target="_blank" rel="noreferrer" data-testid="export-outstanding"><Download className="w-3.5 h-3.5" /> CSV</a>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Candidate</th>
              <th className="px-5 py-3 label-eyebrow">Job</th>
              <th className="px-5 py-3 label-eyebrow">Stage</th>
              <th className="px-5 py-3 label-eyebrow">Deadline</th>
              <th className="px-5 py-3 label-eyebrow">DBS</th>
              <th className="px-5 py-3 label-eyebrow">RTW</th>
              <th className="px-5 py-3 label-eyebrow">Refs</th>
            </tr>
          </thead>
          <tbody>
            {data.outstanding_checks.rows.map((r) => (
              <tr key={r.candidate_id} className="border-t border-[var(--cc-border)]" data-testid={`compliance-row-${r.candidate_id}`}>
                <td className="px-5 py-3 font-medium">{r.candidate_name}</td>
                <td className="px-5 py-3">{r.job_title || "—"}</td>
                <td className="px-5 py-3 capitalize text-xs text-[var(--cc-text-soft)]">{r.stage}</td>
                <td className="px-5 py-3 text-xs">
                  {r.deadline ? <>{fmtDate(r.deadline)}{r.days_overdue > 0 && <span className="ml-2 text-red-500">{r.days_overdue}d overdue</span>}</> : "—"}
                </td>
                <td className="px-5 py-3"><span className={`pill ${r.dbs === "verified" ? "pill-success" : r.dbs === "rejected" ? "pill-danger" : "pill-warning"}`}>{r.dbs.replace(/_/g, " ")}</span></td>
                <td className="px-5 py-3"><span className={`pill ${r.rtw === "verified" ? "pill-success" : r.rtw === "not_eligible" ? "pill-danger" : "pill-warning"}`}>{r.rtw.replace(/_/g, " ")}</span></td>
                <td className="px-5 py-3 text-xs">{r.references}</td>
              </tr>
            ))}
            {data.outstanding_checks.rows.length === 0 && (
              <tr><td colSpan="7" className="text-center py-10 text-[var(--cc-text-soft)]">No outstanding checks 🎉</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
